import React from 'react';
import { IMenuWidgetsProps } from './types';
import { theme } from '../../../../../common/styles/theme';
import { alpha, Box, IconButton, Stack, SxProps, Tooltip } from '@mui/material';
import { ButtonHelp } from './ButtonHelp';
import { DailyGoalHOC } from '../../ToolbarPanel/DailyGoal/hocs/DailyGoalHOC';
import { MenuButton } from '../../ToolbarPanel/Menu/components/MenuButton/MenuButton';
import { OneTimeTooltipTutorialHOC } from '../../OneTimeTooltip/hocs/OneTimeTooltipTutorialHOC/OneTimeTooltipTutorialHOC';
import { PlaylistHOC } from '../../ToolbarPanel/Playlist/hocs/PlaylistHOC';
import { BookIcon } from '../../../../../../icons/BookIcon';
import { LibraryIcon } from '../../../../../../icons/LibraryIcon';
import { RecentIcon } from '../../../../../../icons/RecentIcon';
import { MenuButtonEditVideoHOC } from '../MenuButtonEditVideo/hocs/MenuButtonEditVideoHOC';
import { SettingsIcon } from '../../../../../../icons/SettingsIcon';
import { AccountButtonHOC } from '../../ToolbarPanel/AccountButton/hocs/AccountButtonHOC';
import { ShareIcon } from '../../../../../../icons/ShareIcon';
import { ButtonMenu } from '../../../../../common/components/ButtonMenu/ButtonMenu';
import { IconMenuItem } from '../../../../../common/components/IconMenuItem/IconMenuItem';
import { ListIcon } from '../../../../../../icons/ListIcon';
import { VideoItemIcon } from '../../../../../../icons/VideoItemIcon';
import { DeviceContext } from '../../../../../common/contexts/DeviceContext';
import { EMainMenuActive, EMainViewMode } from '../../../../store/general/types';
import { LangMenuHOC } from '../../ToolbarPanel/LangMenu/hocs/LangMenuHOC';

export const MenuWidgets: React.FC<IMenuWidgetsProps> = ({
	active,
	isHide,
	showRecents,
	mainViewMode,
	onClickContent,
	onClickMyLibrary,
	onClickRecent,
	onShowHelp,
	onClickSettings,
	onCopyLinkVideo,
	onCopyLinkPlaylist,
}) => {

	const { isFullScreen } = React.useContext(DeviceContext);
	if (isFullScreen) return null;

	const isVideo = mainViewMode === EMainViewMode.DASHBOARD;

	const buttonSx: SxProps = {
		width: theme.spacing(4),
		height: theme.spacing(4),
		color: theme.palette.grey[400],
		borderRadius: '50%',
		transition: 'all .3s ease',

		'&.active': {
			background: alpha(theme.palette.common.white, .75),
		},

		'&:hover': {
			background: alpha(theme.palette.common.white, .75),
		},

		'.MuiSvgIcon-root': {
			width: theme.spacing(2.5),
			height: theme.spacing(2.5),
		},
	};

	const [shareOpen, setShareOpen] = React.useState(false);
	const handleShareOpen = () => setShareOpen(true);
	const handleShareClose = () => setShareOpen(false);

	return (
		<Stack
			gap={theme.spacing(1)}
			justifyContent={'space-between'}
			className={`menu-vidgets
				${isHide ? 'hide' : ''}
				${isVideo ? '' : 'bg'}
			`}
			sx={{
				position: 'fixed',
				zIndex: 500,
				left: theme.spacing(1),
				top: theme.spacing(2),
				bottom: theme.spacing(2),

				'&.hide': {
					opacity: .2,
					pointerEvents: 'none',
					transition: 'opacity .3s ease',
				},

				'&.bg': {
					p: theme.spacing(1, 0),

					'&:before': {
						content: '""',
						position: 'absolute',
						zIndex: -1,
						borderRadius: theme.spacing(.75),
						left: theme.spacing(-.25),
						right: theme.spacing(-.25),
						top: theme.spacing(0),
						bottom: theme.spacing(0),
						background: alpha(theme.palette.common.white, .6),
						pointerEvents: 'none',
					},
				},

				'.lang-menu': buttonSx,

				'.lang-flag__code': {
					display: 'none',
				},
			}}
		>
			<Stack
				gap={theme.spacing(1)}
			>
				<Box
					sx={{
						mb: theme.spacing(1),
						'.menu__button': buttonSx,

						'.PrivateSwipeArea-root': {
							width: theme.spacing(.5) + ' !important',
						},
					}}
				>
					<MenuButton
						isLogo={isVideo}
					/>
				</Box>

				{!isVideo &&
					<LangMenuHOC />
				}

				<Tooltip
					placement={'left'}
					title={`Content Library`}
				>
					<IconButton
						className={active===EMainMenuActive.CONTENT_LIBRARY ? 'active' : ''}
						sx={{
							...buttonSx
						}}
						onClick={onClickContent}
					>
						<BookIcon
							sx={{
								color: theme.palette.secondary[200],
							}}
						/>
					</IconButton>
				</Tooltip>

				<Stack
					sx={{
						position: 'relative',

						'&:before': {
							content: '""',
							position: 'absolute',
							zIndex: -1,
							borderRadius: theme.spacing(3),
							left: theme.spacing(-.5),
							right: theme.spacing(-.5),
							top: theme.spacing(-.5),
							bottom: theme.spacing(-.5),
							background: alpha(theme.palette.common.white, .75),
							pointerEvents: 'none',
						},

						'.menu-vidgets.bg &': {
							gap: theme.spacing(1),

							'&:before': {
								display: 'none',
							},
						},
					}}
				>
					<Tooltip
						placement={'left'}
						// title={`My Stuff`}
						title={`My Videos`}
					>
						<IconButton
							className={active===EMainMenuActive.MY_LIBRARY ? 'active' : ''}
							sx={{
								...buttonSx
							}}
							onClick={onClickMyLibrary}
						>
							<LibraryIcon
								sx={{
									width: `${theme.spacing(2.75)} !important`,
									height: `${theme.spacing(2.75)} !important`,
									color: theme.palette.primary.main,
								}}
							/>
						</IconButton>
					</Tooltip>

					{showRecents &&
					<Tooltip
						placement={'left'}
						title={`Recent`}
					>
						<IconButton
							className={active===EMainMenuActive.RECENTS ? 'active' : ''}
							sx={{
								...buttonSx
							}}
							onClick={onClickRecent}
						>
							<RecentIcon
								sx={{
									width: `${theme.spacing(3.2)} !important`,
									height: `${theme.spacing(3.2)} !important`,
									color: theme.palette.secondary[300],
								}}
							/>
						</IconButton>
					</Tooltip>
					}
					
					{isVideo && <>
						<PlaylistHOC isSmall />
					
						<ButtonMenu
							buttonProps={{
								sx: buttonSx,
								children: <ShareIcon
									sx={{
										width: `${theme.spacing(2.25)} !important`,
										height: `${theme.spacing(2.25)} !important`,
										color: theme.palette.grey[500],
									}}
								/>
							}}
							menuProps={{
								keepMounted: true,
								open: !!shareOpen,
								anchorOrigin: {
									vertical: 'top',
									horizontal: 'right', 
								},
								transformOrigin: {
									vertical: 'top',
									horizontal: 'left', 
								},
								sx: {
									ml: theme.spacing(.5),
									mt: theme.spacing(-3.5),
								},
							}}
							onOpen={handleShareOpen}
							onClose={handleShareClose}
						>
							<>
								<IconMenuItem
									title={'Share video'}
									onClick={onCopyLinkVideo}
								>
									<VideoItemIcon />
								</IconMenuItem>
								<IconMenuItem
									title={'Share playlist'}
									onClick={onCopyLinkPlaylist}
								>
									<ListIcon />
								</IconMenuItem>
							</>
						</ButtonMenu>
					</>}
				</Stack>

				<MenuButtonEditVideoHOC />
			
			</Stack>

			<Stack
				gap={theme.spacing(1)}
			>
				<DailyGoalHOC isSmall />

				<Tooltip
					placement={'left'}
					title={`Settings`}
				>
					<IconButton
						sx={buttonSx}
						onClick={onClickSettings}
					>
						<SettingsIcon
							sx={{
								width: `${theme.spacing(3)} !important`,
								height: `${theme.spacing(3)} !important`,
								color: theme.palette.grey[400],
							}}
						/>
					</IconButton>
				</Tooltip>

				<ButtonHelp onClick={onShowHelp} />
				<OneTimeTooltipTutorialHOC />
				<AccountButtonHOC isSmall />
			</Stack>
		</Stack>
	);
}
